import {Accordion, Col, Row, Form, InputGroup} from "react-bootstrap";
import {ADMIN_LANG} from "../../../../settings";
import {PlusCircle, Trash} from "react-bootstrap-icons";
import React from "react";
import {v4 as uuidv4} from "uuid";
import {changeGameModal} from "../../../../app/slices/modals/gameEditModal";
import {useAppDispatch, useAppSelector} from "../../../../app/store";
import AsyncTeamsSelect from "../../gameView/AsyncTeamsSelect";
import {PlainDetails, PointRange} from "../../../../types/admin";

type Props = {
    languageTab: string;
}

const GameSettings = ({languageTab}: Props) => {
    const dispatch = useAppDispatch();
    const {
        end_points,
        display_correct_answers,
        min_points,
        max_points,
        teams,
        restrict_playing
    } = useAppSelector((state) => state.gameEditModal);

    const handleCorrectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(changeGameModal({display_correct_answers: e.target.checked}));
    }

    const handleRestictChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(changeGameModal({restrict_playing: e.target.checked}));
    }

    const handleMinPointsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(changeGameModal({min_points: parseInt(e.target.value)}));
    }

    const handleOptionEndChange = (option_id: string, end: string) => {
        const updatedEndPoints = end_points.map((option: PointRange) => {
            if (option.range_id === option_id) {
                return {...option, percentage: parseInt(end)};
            }
            return option;
        });
        dispatch(changeGameModal({end_points: updatedEndPoints}));
    }

    const handleEndPointsTextChange = (option_id: string, language_code: string, message: string) => {
        const updatedEndPoints = end_points.map((option) => {
            if (option.range_id === option_id) {
                const updatedLanguages = option.languages.map((lang) => {
                    if (lang.language_code === language_code) {
                        return {...lang, message};
                    }
                    return lang;
                });
                return {...option, languages: updatedLanguages};
            }
            return option;
        });

        dispatch(changeGameModal({end_points: updatedEndPoints}));
    }

    const handleRemoveOption = (option_id: string) => {
        dispatch(changeGameModal({end_points: end_points.filter((option) => option.range_id !== option_id)}))
    }

    const handleAddOption = () => {
        const newOption = {
            key: uuidv4(),
            range_id: uuidv4(),
            percentage: 0,
            languages: [{language_code: languageTab, message: ''}]
        }
        dispatch(changeGameModal({end_points: [...end_points, newOption]}));
    }

    const handleTeamsChange = (teams: PlainDetails[]) => {
        dispatch(changeGameModal({teams}));
    }

    const minPoints = min_points >= 0 && min_points <= 100 ? (min_points / 100) * max_points : 0;

    return (
        <Accordion.Body>
            <Row className='gy-3'>
                <Col xs={12} md={6}>
                    <Form.Group controlId={`min_points_${languageTab}`}>
                        <Form.Label>{ADMIN_LANG.min_points}</Form.Label>
                        <InputGroup>
                            <Form.Control type="number"
                                          onChange={handleMinPointsChange}
                                          value={min_points}
                                          min={0}
                                          max={100}/>
                            <InputGroup.Text>%</InputGroup.Text>
                        </InputGroup>
                        <div className='help-text'>
                            {ADMIN_LANG.required_points} {minPoints.toFixed(0)} / {max_points}
                        </div>
                    </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                    <Form.Group controlId={`teams_${languageTab}`}>
                        <Form.Label>{ADMIN_LANG.allowed_teams}</Form.Label>
                        <AsyncTeamsSelect onChange={handleTeamsChange} initialValues={teams}/>
                        <Form.Text className='help-text'>{ADMIN_LANG.team_help}</Form.Text>
                    </Form.Group>
                </Col>
                <Col xs={12} md={6}>
                    <Form.Group className='pb-3' controlId={`display_correct_answers_${languageTab}`}>
                        <Form.Switch
                            className='form-label'
                            onChange={handleCorrectChange}
                            checked={display_correct_answers}
                            label={ADMIN_LANG.display_correct_answers}
                        />
                        <Form.Text className='help-text'>{ADMIN_LANG.display_correct_help}</Form.Text>
                    </Form.Group>
                    <Form.Group controlId={`restrict_playing_${languageTab}`}>
                        <Form.Switch
                            className='form-label'
                            onChange={handleRestictChange}
                            checked={restrict_playing}
                            label={ADMIN_LANG.restrict_playing}
                        />
                        <Form.Text className='help-text'>{ADMIN_LANG.restrict_playing_help}</Form.Text>
                    </Form.Group>
                </Col>


                <Col xs={12}>
                    <h4 className='mb-0'>
                        {ADMIN_LANG.end_screen_points}
                        <PlusCircle
                            onClick={handleAddOption}
                            className='utility-icon green ms-1'/>
                    </h4>
                    <Form.Text className='help-text'>{ADMIN_LANG.end_screen_points_help}</Form.Text>
                    <Row className='gy-3 pt-3'>
                        <Col xs={8}>
                            <Form.Label
                                className='mb-0'>{ADMIN_LANG.point_range_text} ({languageTab.toUpperCase()})</Form.Label>
                        </Col>
                        <Col xs={2}>
                            <Form.Label className='mb-0'>Max %</Form.Label>
                        </Col>
                        {end_points.map((option) => {
                            const endPointsValue = option.percentage >= 0 && option.percentage <= 100
                                ? (option.percentage / 100) * max_points
                                : 0;

                            return (
                                <Col key={option.key} xs={12}>
                                    <Row>
                                        <Col xs={8}>
                                <textarea
                                    value={option.languages.find((lang) => lang.language_code === languageTab)?.message || ''}
                                    onChange={(e) => handleEndPointsTextChange(option.range_id, languageTab, e.target.value)}
                                    className="form-control"
                                />
                                        </Col>
                                        <Col xs={2}>
                                            <InputGroup>
                                                <Form.Control
                                                    type="number"
                                                    onChange={(e) => handleOptionEndChange(option.range_id, e.target.value)}
                                                    value={option.percentage}
                                                    min={0}
                                                    max={100}
                                                />
                                                <InputGroup.Text>%</InputGroup.Text>
                                                <Form.Text className="help-text">
                                                    {ADMIN_LANG.required_points} {endPointsValue.toFixed(0)}
                                                </Form.Text>
                                            </InputGroup>
                                        </Col>
                                        <Col xs={2} className="d-flex justify-content-center align-items-start pt-2">
                                            <Trash onClick={() => handleRemoveOption(option.range_id)}
                                                   className="utility-icon red"/>
                                        </Col>
                                    </Row>
                                </Col>
                            );
                        })}
                    </Row>

                </Col>
            </Row>
        </Accordion.Body>

    )
}

export default GameSettings;